import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface ImageViewerItem {
  url: string
  alt?: string
  title?: string
}

interface Props {
  items: ImageViewerItem[]
  initialIndex: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ImageViewerDialog = ({
  items,
  initialIndex,
  open,
  onOpenChange,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  const currentItem = items[currentIndex]
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < items.length - 1

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        handleNext()
      }
    }

    if (open) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, currentIndex, hasPrevious, hasNext])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[95vw] h-[85vh] max-w-[95vw] max-h-[85vh] p-0'>
        <div className='flex flex-col w-full h-full'>
          <div className='flex items-center gap-4 px-2 py-1 h-12 border-b shrink-0'>
            {items.length > 1 ? (
              <>
                <div className='flex items-center gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={handlePrevious}
                    disabled={!hasPrevious}
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={handleNext}
                    disabled={!hasNext}
                  >
                    <ChevronRight className='h-4 w-4' />
                  </Button>
                </div>

                <span className='text-xs text-muted-foreground shrink-0'>
                  {currentIndex + 1} / {items.length}
                </span>

                <span className='text-sm truncate min-w-0 flex-1'>
                  {currentItem?.title}
                </span>
              </>
            ) : (
              <span className='text-sm truncate min-w-0 max-w-[calc(95vw-60px)] flex-1 pl-2'>
                {currentItem?.title}
              </span>
            )}
          </div>

          <figure className='flex-1 min-h-0 m-0'>
            <img
              src={currentItem?.url}
              alt={currentItem?.alt ?? currentItem?.title}
              className='object-contain w-full h-full'
            />
          </figure>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ImageViewerDialog
